import { DOCUMENT, NgComponentOutlet } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, computed, inject, input, viewChild, viewChildren, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MetadataEntityFieldData, MetadataEntityFieldEditorType } from '@jms/model';
import { PAGE_COMPONENTS, PageMode } from '@workbuddy/ui/blocks/page';
import { ContentListComponent, ContentListItemComponent } from '@workbuddy/ui/components/content-list';
import { RoomPresenceComponent } from '@workbuddy/ui/components/room.presence';
import { ActionBarComponent } from '@workbuddy/ui/v2/components/action.bar';
import { WbSurfaceSaveStatusComponent } from '../../core/surface/wb.surface.save.status.component';
import type { SurfaceBreadcrumb } from '../../core/contracts/surface.capabilities';
import type { FieldMode } from '../../core/contracts/field.binding';
import { WbFieldComponent } from '../../core/field/wb.field.component';
import type { SurfaceContextHandle } from '../../core/runtime/surface.context/create.surface.context.options';
import { CompiledRepeat } from '../../core/runtime/compiled.form';
import { repeatTableFromCompiledRepeat } from '../../core/repeat.table/repeat.table.adapter';
import { RepeatTableConfig } from '../../core/repeat.table/repeat.table.model';
import { WbRepeatTableComponent } from '../../core/repeat.table/wb.repeat.table.component';
import { WbSectionComponent } from '../../core/sections/wb.section.component';
import type { SectionFieldDeclaration } from '../../core/sections/wb.section.component';
import { resolveSurfaceActions, toSurfaceActionCommands } from '../../core/surface/surface.action.presenter';
import { resolveSurfaceBanner, resolveSurfaceBreadcrumbs, withInSurfaceBreadcrumb } from '../../core/surface/surface.chrome.presenter';
import { SectionRenderInfo } from '../../core/surface/surface.section.render.info';
import { buildSectionRenderMap, childSectionPaths, settingsNavigationGroups, visibleSectionsForPaths } from '../../core/surface/surface.section.presenter';
import { SETTINGS_NAV_QUERY_PARAM, SETTINGS_SEARCH_QUERY_PARAM, settingsNavPathFromQuery, settingsNavQueryValue } from './settings.location';
import { SETTINGS_PRESENCE_ROOM } from './settings.presence';
import { matchSettingsSearch, SETTINGS_SEARCH_DEBOUNCE_MS } from './settings.search';
import { SettingsSearchHighlightPipe } from './settings.search.highlight.pipe';
import { mountSettingsHostTheme } from './wb.surface.settings.host.theme';

interface SettingsFieldRow {
  readonly key: string;
  readonly label: string;
  readonly description: string;
  readonly declaration: SectionFieldDeclaration;
  readonly editorMetadata: MetadataEntityFieldData;
  readonly wide: boolean;
}

interface SettingsSectionRender {
  readonly info: SectionRenderInfo;
  readonly rows: readonly SettingsFieldRow[];
}

@Component({
  selector: 'wb-surface-settings-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [NgComponentOutlet, RouterLink, PAGE_COMPONENTS, ContentListComponent, ContentListItemComponent, ActionBarComponent, WbFieldComponent, WbRepeatTableComponent, WbSectionComponent, WbSurfaceSaveStatusComponent, RoomPresenceComponent, SettingsSearchHighlightPipe],
  templateUrl: './wb.surface.settings.host.component.html',
  styleUrls: ['./wb.surface.settings.host.component.less'],
  host: {
    '[attr.data-surface]': '"settings"',
  },
})
export class WbSurfaceSettingsHostComponent {

  protected readonly PageMode = PageMode;
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly pageTitle = viewChild<ElementRef<HTMLHeadingElement>>('pageTitle');
  private readonly searchBox = viewChild<ElementRef<HTMLInputElement>>('searchBox');
  private readonly navigationItems = viewChildren<ElementRef<HTMLElement>>('navigationItem');
  private returnNavigationIndex = -1;
  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  public readonly $handle = input.required<SurfaceContextHandle>({ alias: 'handle' });
  protected readonly $presenceRoom = inject(SETTINGS_PRESENCE_ROOM, { optional: true });
  private readonly $query = toSignal(this.route.queryParamMap, { initialValue: this.route.snapshot.queryParamMap });

  protected readonly $page = computed(() => this.$handle().page);
  protected readonly $surfaceMode = computed<FieldMode>(() => this.$handle().surface.defaultFieldMode);
  protected readonly $surfaceErrors = computed(() => this.$page().$surfaceErrors());

  private readonly $sectionRenderInfoByPath = computed(() => {
    return buildSectionRenderMap(this.$handle());
  });

  private readonly $selectedNavigationPath = computed(() => {
    return settingsNavPathFromQuery(this.$navigation().map(section => section.info.section.path), this.$query().get(SETTINGS_NAV_QUERY_PARAM));
  });
  protected readonly $searchQuery = computed(() => (this.$query().get(SETTINGS_SEARCH_QUERY_PARAM) ?? '').trim());

  protected readonly $navigation = computed(() => this.visibleSections(this.$handle().compiled.namedSlotSections['navigation'] ?? []));
  protected readonly $navigationGroups = computed(() => settingsNavigationGroups(this.$navigation(), section => section.info.section.group));
  protected readonly $content = computed(() => this.visibleSections(this.$handle().compiled.namedSlotSections['content'] ?? []));
  protected readonly $selectedNavigation = computed(() => {
    const path = this.$selectedNavigationPath();
    return path ? this.$navigation().find(section => section.info.section.path === path) ?? null : null;
  });
  protected readonly $searching = computed(() => this.$searchQuery().length > 0);
  protected readonly $displayContent = computed(() => {
    if (this.$searching()) return [];
    const selected = this.$selectedNavigation();
    if (!selected) return this.$content();
    const children = this.visibleSections(childSectionPaths(selected.info.section.path, this.$handle().compiled.sections));
    return children.length > 0 ? children : [selected];
  });

  private readonly $searchCatalog = computed(() => {
    const compiled = this.$handle().compiled.sections;
    return this.$navigation().flatMap(area => {
      const sections = this.sectionsForArea(area, compiled)
        .map(section => ({
          path: section.info.section.path,
          title: section.info.$title() ?? section.info.section.path,
          description: section.info.section.description ?? '',
          fields: section.rows.filter(row => !row.declaration.instance?.$hidden()),
        }))
        .filter(section => section.fields.length > 0);
      if (sections.length === 0) return [];
      return [{
        path: area.info.section.path,
        title: area.info.$title() ?? area.info.section.path,
        description: area.info.section.description ?? '',
        sections,
      }];
    });
  });

  protected readonly $searchHits = computed(() => matchSettingsSearch(this.$searchQuery(), this.$searchCatalog()));

  protected readonly $pageTitle = computed(() => {
    if (this.$searching()) return null;
    const selected = this.$selectedNavigation();
    if (selected) return selected.info.$title() ?? selected.info.section.path;
    const source = this.$breadcrumbsSource();
    const crumbs = source ? source() : [];
    return crumbs[crumbs.length - 1]?.label ?? null;
  });

  protected readonly $breadcrumbs = computed<readonly SurfaceBreadcrumb[]>(() => {
    const source = this.$breadcrumbsSource();
    const crumbs = source ? source() : [];
    if (this.$searching()) return withInSurfaceBreadcrumb(crumbs, 'Search', () => this.clearSearch());
    const selected = this.$selectedNavigation();
    const current = selected?.info.$title() ?? selected?.info.section.path ?? null;
    return withInSurfaceBreadcrumb(crumbs, current, () => this.closeNavigation());
  });

  private readonly $breadcrumbsSource = computed(() => resolveSurfaceBreadcrumbs(this.$handle().resolver));

  protected readonly $banner = computed(() => resolveSurfaceBanner(this.$handle().resolver));

  private readonly $resolvedCommands = computed(() => resolveSurfaceActions(this.$handle()));

  protected readonly $actionCommands = computed(() => toSurfaceActionCommands(this.$resolvedCommands(), this.$page().$saving()));

  public constructor() {

    mountSettingsHostTheme(this.document);
    this.destroyRef.onDestroy(() => this.clearSearchTimer());
    afterNextRender(() => this.syncSearchBox(this.route.snapshot.queryParamMap.get(SETTINGS_SEARCH_QUERY_PARAM) ?? ''));
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => this.syncSearchBox(params.get(SETTINGS_SEARCH_QUERY_PARAM) ?? ''));
  }

  protected openNavigation(section: SettingsSectionRender): void {

    this.returnNavigationIndex = this.$navigation().findIndex(item => item.info.section.path === section.info.section.path);
    void this.writeLocation({ [SETTINGS_NAV_QUERY_PARAM]: settingsNavQueryValue(section.info.section.path) });
    setTimeout(() => this.pageTitle()?.nativeElement.focus());
  }

  protected closeNavigation(): void {

    const index = this.returnNavigationIndex;
    void this.writeLocation({ [SETTINGS_NAV_QUERY_PARAM]: null });
    setTimeout(() => this.navigationItems()[index]?.nativeElement.focus());
  }

  protected onSearchInput(event: Event): void {

    const value = (event.target as HTMLInputElement).value;
    this.scheduleSearch(value);
  }

  protected onSearchKeydown(event: KeyboardEvent): void {

    if (event.key !== 'Escape') return;
    if (!this.$searchQuery() && !(this.searchBox()?.nativeElement.value ?? '').trim()) return;
    event.preventDefault();
    this.clearSearch();
  }

  protected clearSearch(): void {

    this.clearSearchTimer();
    const input = this.searchBox()?.nativeElement;
    if (input) input.value = '';
    void this.writeLocation({ [SETTINGS_SEARCH_QUERY_PARAM]: null });
  }

  /**
   * Section-level validation messages for a plain-row section. wb-section shows its own; rows rendered
   * directly by this host had no outlet, so a message about a stored value (e.g. a default job structure
   * whose addon was switched off) was computed but never seen.
   */
  protected sectionErrors(section: SettingsSectionRender): readonly { code: string; message: string }[] {

    try {
      return this.$page()?.section(section.info.section.path).$errors() ?? [];
    } catch {
      return [];
    }
  }

  protected repeatTableConfig(repeat: CompiledRepeat): RepeatTableConfig {

    return repeatTableFromCompiledRepeat(repeat);
  }

  private scheduleSearch(value: string): void {

    this.clearSearchTimer();
    const trimmed = value.trim();
    if (!trimmed) {
      void this.writeLocation({ [SETTINGS_SEARCH_QUERY_PARAM]: null });
      return;
    }
    this.searchTimer = setTimeout(() => {
      void this.writeLocation({ [SETTINGS_SEARCH_QUERY_PARAM]: trimmed });
      this.searchTimer = null;
    }, SETTINGS_SEARCH_DEBOUNCE_MS);
  }

  private syncSearchBox(query: string): void {

    const input = this.searchBox()?.nativeElement;
    if (input && input.value !== query) input.value = query;
  }

  private writeLocation(queryParams: Record<string, string | null>): Promise<boolean> {

    return this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private clearSearchTimer(): void {

    if (!this.searchTimer) return;
    clearTimeout(this.searchTimer);
    this.searchTimer = null;
  }

  private sectionsForArea(area: SettingsSectionRender, compiled: readonly { readonly path: string }[]): readonly SettingsSectionRender[] {

    const children = this.visibleSections(childSectionPaths(area.info.section.path, compiled));
    return children.length > 0 ? children : [area];
  }

  private visibleSections(paths: readonly string[]): readonly SettingsSectionRender[] {

    return visibleSectionsForPaths(paths, this.$sectionRenderInfoByPath(), this.$page())
      .map(info => ({
        info,
        rows: info.fields.map(declaration => this.toFieldRow(declaration)),
      }));
  }

  private toFieldRow(declaration: SectionFieldDeclaration): SettingsFieldRow {

    const metadata = declaration.metadata;
    return {
      key: declaration.instance?.path ?? declaration.field.bind,
      label: metadata.displayName ?? metadata.name ?? declaration.field.bind,
      description: metadata.description ?? '',
      declaration,
      editorMetadata: {
        ...metadata,
        displayName: '',
        description: '',
      },
      wide: !!declaration.customRenderer || metadata.editor === MetadataEntityFieldEditorType.MergeInput,
    };
  }
}
